import { useEffect, useState } from 'react';
import { endpoints } from '../../api';
import type { CircleFormState } from './circle/CircleFieldInput';
import type { PhotoSlot } from './StudioPhotoTray';

export function useStudioPhotoPreviews(state: CircleFormState, slots: PhotoSlot[]) {
  const [result, setResult] = useState<{ identity: string; urls: Record<string, string>; error: boolean }>({
    identity: '',
    urls: {},
    error: false,
  });
  const entries = slots.filter(slot => state.inputs[slot.key]).map(slot => [slot.key, state.inputs[slot.key]]);
  const identity = JSON.stringify([state.ownerId, entries]);
  const [attempt, retry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    const urls: string[] = [];
    let disposed = false;
    const [owner, requested] = JSON.parse(identity) as [number | null, Array<[string, string]>];
    if (!owner || !requested.length) return;
    const timer = setTimeout(() => controller.abort(), 30000);
    void (async () => {
      const me = await endpoints.me();
      if (me.user.id !== owner || controller.signal.aborted) throw new Error('owner_changed');
      const results = await Promise.allSettled(
        requested.map(async ([key, id]) => {
          const blob = await endpoints.uploadPreview(id, controller.signal);
          if (disposed || controller.signal.aborted) throw new Error('preview_cancelled');
          if (!['image/jpeg', 'image/png', 'image/webp'].includes(blob.type) || !blob.size || blob.size > 20 * 1024 * 1024)
            throw new Error('preview_invalid');
          const url = URL.createObjectURL(blob);
          urls.push(url);
          return [key, url] as const;
        }),
      );
      if (!disposed && !controller.signal.aborted)
        setResult({
          identity,
          urls: Object.fromEntries(results.flatMap(result => (result.status === 'fulfilled' ? [result.value] : []))),
          error: results.some(result => result.status === 'rejected'),
        });
    })()
      .catch(() => {
        if (!disposed) setResult({ identity, urls: {}, error: true });
      })
      .finally(() => clearTimeout(timer));
    return () => {
      disposed = true;
      controller.abort();
      clearTimeout(timer);
      urls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [identity, attempt]);
  return {
    urls: result.identity === identity ? result.urls : {},
    error: result.identity === identity && result.error,
    retry: () => retry(value => value + 1),
  };
}
/** Снимок входа заказа для героя хвоста («снимок размыт», APP-SPEC-5 §2.5), пока сервер не прислал display.picture:
    превью загрузки по UUID из самого заказа (переживает перезагрузку страницы). enabled — только пока герой виден. */
export function useStudioOrderSourcePreview(state: CircleFormState, key: string, enabled: boolean): string | undefined {
  const id = enabled ? (state.intent?.body.inputs as Record<string, unknown> | undefined)?.[key] : undefined;
  const slots: PhotoSlot[] = typeof id === 'string' && id ? [{ key, type: 'upload', required: false, values: [], upload: null }] : [];
  const previews = useStudioPhotoPreviews({ ...state, inputs: slots.length ? { [key]: id as string } : {} }, slots);
  return previews.urls[key];
}
